import { useState, type CSSProperties } from "react";
import type { MediaStreamLike } from "@cuekards/ui";
import { displayLabel } from "@cuekards/core/media-devices";
import type { DeviceState } from "@cuekards/core/media-devices";
import type { LoadResult } from "@cuekards/core/script-source";
import { DeviceCircle, type DeviceCircles } from "./device-circles.js";
import {
  CLEAR_CONFIRM,
  DOC_SHARING_HINT,
  LOAD_FROM_DOC,
  OVERWRITE_CONFIRM,
  SAMPLE_SCRIPT,
  TRY_A_SAMPLE,
} from "@cuekards/core/script-copy";
import {
  CAMERA_OFF_NOTE,
  MIC_OFF_NOTE,
  stageWell,
  startButton,
  type CameraStage,
} from "./green-room.js";
import { CameraIcon, CameraOffIcon } from "./icons.js";
import { Modal } from "./Modal.js";
import { meterBars, useMicLevel } from "./mic-level.js";
import { useFrameAspect, useSelfView } from "./self-view.js";
import {
  HOME,
  HOW_IT_WORKS,
  MARK_ICON,
  MARK_ICON_BOX,
  MARK_IMAGE,
  MARK_IMAGE_BOX,
  NEW_TAB,
  PRIVACY,
  PRIVACY_MORE,
  PRIVACY_SHORT,
  SITE_MARK,
  TOP_NAV_LABEL,
} from "./site-links.js";

/**
 * The green room (web-ui ticket 05): the screen you arrive on.
 *
 * It replaces `SetupScreen`, which was a page you read top to bottom and filled
 * in — a headline, a tagline, a walkthrough, two forms, a download offer, a
 * privacy card and a signup card. The camera was never on it, because until
 * ticket 04 the stream existed only inside `CaptureEngine` and no `<video>`
 * element existed anywhere in this product. That is the whole problem in one
 * sentence: a teleprompter that never shows you your own face reads like a form,
 * not like a camera you stand in front of.
 *
 * Two columns and a camera instead. **The page does not scroll**, at 1440×900 and
 * at 1280×800; the product is desktop-only by capability gate, so there is no
 * phone breakpoint to answer for.
 *
 * The two columns swapped over in flow ticket 04, and that inversion is what this
 * screen is now: the camera is a *preview* at a fixed size with the two device
 * switches on it, and the script — the thing a visitor came here to paste — is the
 * wide one. The control bar the prompter has is not mounted here any more; a
 * floating transport in front of a screen you are typing into is furniture from
 * the wrong screen, and what this screen has instead is one action under the box.
 *
 * Everything here arrives through props. The camera itself is the host's — the
 * stream comes out of `useCapture` in `WebApp`, above all three screens, because
 * a step into the settings must not take a live preview down with it.
 */

export function GreenRoom({
  link,
  text,
  loading,
  error,
  summary,
  stage,
  preview,
  devices,
  circles,
  micOff,
  onLink,
  onText,
  onLoad,
  onClear,
  onGrant,
  onRetry,
  onStart,
  recovery,
  follow,
  model,
}: {
  link: string;
  text: string;
  loading: boolean;
  error?: string;
  /** The line under the script box, or null with nothing loaded. */
  summary: string | null;
  /** Which of the five the camera is in, decided in `WebApp` — the same reading
   * the control bar's device toggles come from, so the stage and the bar cannot
   * disagree about whether there is a camera. */
  stage: CameraStage;
  preview: { stream: MediaStreamLike | null; error: string | null };
  devices: DeviceState | null;
  /**
   * The two device switches, drawn over the preview since flow ticket 04, or
   * `null` where there is nothing pressable to draw.
   *
   * Decided by `roomToggles` in `WebApp` and handed down — the *same two objects*
   * the prompter's control bar is given, so the room and the take cannot disagree
   * about which device is open. Nothing about the decision lives on this screen,
   * and there is no `useState` behind either button: the press goes to the worker,
   * the worker says what is off, and the preview reads that answer back. That is
   * what lets a take borrow the camera this screen already has open (ADR-0024).
   *
   * **`null` rather than a list of stage names in this component**, which is
   * `stageControls` and is the same argument one step further: which states have
   * something pressable is a fact about the toggles, and a component checking the
   * stage would be a second copy of it that can drift. See that function for the
   * two states the obvious list gets wrong.
   */
  circles: DeviceCircles | null;
  /**
   * Whether the microphone has been switched off, for the note under the stage.
   *
   * Read off the device state by the host rather than off `circles.microphone.on`
   * beside it, because that circle cannot answer it: a browser that refused the
   * hardware hands over a circle that is `on: false` *because there is no
   * permission*, with nobody having switched anything — and the note would then
   * be under a microphone nobody muted.
   */
  micOff: boolean;
  onLink: (link: string) => void;
  onText: (text: string) => void;
  /** Fetch a document and say what came back — see {@link ScriptCard}, which is
   * the only caller and puts the text it gets into the box. */
  onLoad: (reference: string) => Promise<LoadResult>;
  onClear: () => void;
  onGrant: () => void;
  onRetry: () => void;
  /** The room's one action (flow ticket 05): hand whatever is in the box over,
   * and open the prompter. It starts no take. */
  onStart: () => void;
  /** The interrupted-take offer, where there is one. Above the script card,
   * pushing it down: a take that was already shot and cannot be shot again is
   * the most important thing on this screen on the rare visit it appears. */
  recovery: JSX.Element;
  /**
   * `Follow my voice` (flow ticket 06): the one thing this product does that no
   * other teleprompter does, as a box you tick.
   *
   * Under the action rather than over it, because it is a choice about *how* the
   * take will read and not a step on the way to one — and above {@link model},
   * whose panel is now what happens after this is ticked rather than the thing
   * that offers it.
   */
  follow: JSX.Element;
  /** The speech model's progress and its failures — under the box that starts
   * one, so it reads as what is happening and not as a gate. That is the
   * existing argument for its position and it survives. */
  model: JSX.Element;
}): JSX.Element {
  return (
    <main className="web-room">
      <header className="web-room-head">
        {/* The screen's name, for a reader who cannot see that it is a room (seo
          * ticket 08). Not the wordmark promoted to a heading: "cuekards" names
          * the product and this has to name the *screen*, the way `Settings` and
          * `How it works` do on the two beside it — and the mark is a mark, in
          * the corner, which is a design decision rather than a document
          * outline. Visually hidden because `/app` is noindex and the room's
          * design is finished; see `web.css` on why that is not `display: none`. */}
        <h1 className="web-visually-hidden">Set up your take</h1>
        {/* The mark home and one link to the walkthrough, which is what the three
          * documents' bar carried from flow ticket 01 until signal-and-paper
          * ticket 06 grew it to three links and a button — so the site and the
          * application are joined back up rather than merely separated (ADR-0025).
          * Somebody who followed the front page's call to action could not get
          * back to it before this; the mark was a `<span>`, and inert.
          *
          * **The two bars are no longer word for word**, and that is named here
          * rather than fixed: the documents' bar now offers `/privacy` and the
          * questions and repeats the call to action, and none of the three is
          * something to put in front of somebody who is already in the
          * application. What the two still share is the mark and the walkthrough,
          * which is what `site-links.ts` holds.
          *
          * **The mark is the same picture as the three static pages' now**, rather
          * than the product's name set in the application's own font: `MARK_IMAGE`
          * and `MARK_IMAGE_BOX` repeat `logo.ts`'s served path and box, which
          * `page/` cannot import directly (`ships-to-the-browser.test.ts`). `alt`
          * still carries `SITE_MARK`, so a reader who cannot see the picture is
          * told the same word. The card before it is `MARK_ICON`, the favicon and
          * the phone's launcher icon, with an empty `alt` because the lettering
          * already says the name.
          *
          * **Both open a tab of their own**, and that is the whole of why a room
          * is allowed a link out at all: `NEW_TAB` is not a navigation away from
          * this tab, so nothing here can take a camera down or end a take. The
          * room is the calm case — the prompter, where the same link sits over a
          * recording, is the one the rule was written for. */}
        <a className="web-room-mark" href={HOME} {...NEW_TAB}>
          <img
            src={MARK_ICON}
            alt=""
            width={MARK_ICON_BOX.width}
            height={MARK_ICON_BOX.height}
          />
          <img
            src={MARK_IMAGE}
            alt={SITE_MARK}
            width={MARK_IMAGE_BOX.width}
            height={MARK_IMAGE_BOX.height}
          />
        </a>
        {/* `web-how-link` beside the new class, because that is the handle four
          * browser drivers press this by — an anchor now rather than the `?`
          * circle it was, since the room has the space to say the words and the
          * documents' bar says them too. The `⚙` that stood next to it is gone:
          * the settings are reachable from the prompter, which is the decision
          * the flow spec records. */}
        <a className="web-room-how web-how-link" href={HOW_IT_WORKS} {...NEW_TAB}>
          {TOP_NAV_LABEL}
        </a>
      </header>

      <div className="web-room-columns">
        {/* The preview and what has to be said under it, as one column. Both notes
          * are *under* rather than in: what a device being off costs is about the
          * take you are still going to record, not about the hole where the
          * picture was, and each has to still be readable when your eye is on the
          * button that starts one.
          *
          * The camera's is on `camera-off` and not on `opening`, although the two
          * draw the same slot: this sentence is about a choice somebody made, and
          * a camera that is merely waking up has not made it. */}
        <div className="web-room-stage">
          {/* The box the well is fitted inside, and *only* the well.
            *
            * `100cqw` and `100cqh` read this element rather than the column, which
            * is what makes the two notes safe. They used to share a container, and
            * that was sound only because `has-frame` and the camera-off note could
            * never be on screen at once — a note appeared exactly when the picture
            * did not. The mic-off note arrives here from the control bar's dock in
            * flow ticket 04 and is drawn *while the camera is live*, so a portrait
            * frame taking the full `100cqh` with a sentence under it would be the
            * room scrolling. Its own size comes from the column and the page, never
            * from what is in it, so nothing here can loop. */}
          <div className="web-room-preview">
            <CameraStageView
              stage={stage}
              preview={preview}
              devices={devices}
              circles={circles}
              onGrant={onGrant}
              onRetry={onRetry}
            />
          </div>
          {stage === "camera-off" && (
            <p className="web-stage-note is-camera">{CAMERA_OFF_NOTE}</p>
          )}
          {/* It rode the control bar's dock until flow ticket 04, and the room no
            * longer has one. It is the more important of the two: a microphone
            * switched off is a prompter that stops moving three minutes into a read
            * for a reason nothing else on screen explains.
            *
            * `role="status"` rather than `alert` — nothing has gone wrong, and it
            * describes a state the user put the room in on purpose. */}
          {micOff && (
            <p className="web-stage-note is-mic" role="status">
              {MIC_OFF_NOTE}
            </p>
          )}
        </div>

        <div className="web-room-side">
          {recovery}
          <ScriptCard
            link={link}
            text={text}
            loading={loading}
            error={error}
            summary={summary}
            onLink={onLink}
            onText={onText}
            onLoad={onLoad}
            onClear={onClear}
          />
          {/* What the product does with your video, said where somebody needs
            * hearing it (flow ticket 09).
            *
            * This screen is a camera preview of your own face, a box you are
            * about to paste a script into, and one button that says
            * `START RECORDING`. Everything a stranger reads before this point is
            * on `/`, which they may not have come through at all — `/app` is an
            * address, and the front page's call to action is one press back for
            * somebody who did. Between flow ticket 02 deleting `HelpPage` and
            * this line, the application said nothing about privacy on any screen.
            *
            * **Under the box rather than in the bar.** ADR-0026 put the room's
            * header and the documents' bar word for word on the argument that the
            * sameness is the feature, and signal-and-paper ticket 06 gave the
            * documents a second and third link and a button — so what is shared is
            * now the mark and the walkthrough rather than the whole bar. This
            * sentence stays out of the header regardless: the documents reach
            * `/privacy` through their own furniture, and this is the moment the
            * sentence is worth anything anyway.
            *
            * The same pair `documents.ts` renders on `/` and on
            * `/how-it-works`: reassurance, then the one link to the argument. And
            * `NEW_TAB` like every other anchor this module hands out — the room is
            * not recording, so this one *could* be an ordinary navigation, but an
            * anchor that is the odd one out is the one somebody copies later onto
            * a screen where it matters. */}
          <p className="web-room-privacy">
            {PRIVACY_SHORT}{" "}
            <a href={PRIVACY} {...NEW_TAB}>
              {PRIVACY_MORE}
            </a>
          </p>
          <StartButton text={text} loading={loading} onStart={onStart} />
          {follow}
          {model}
        </div>
      </div>
    </main>
  );
}

/**
 * The camera well: the video, or the reason there is not one.
 *
 * **The well is the shape of the frame being recorded.** Nothing on the recording
 * path crops or reframes anything — the recorder is handed the raw stream — so the
 * only way the picture on this screen can disagree with the file is this element's
 * box, and it used to: a stage sized by the page's flex arithmetic, at whatever
 * ratio a window happened to leave over, with the video `cover`ed into it. A 4:3
 * camera in a 2:1 well showed the middle two thirds and recorded all of it, and
 * the way you found out was by opening the file. So the ratio comes off the
 * element and onto the section, and the well fits the camera rather than the
 * column.
 *
 * Only once there is a picture, which is what `has-frame` says. The four
 * pictureless states are not a frame of anything and keep the whole column, so the
 * room does not open at some guessed 16:9.
 *
 * The video stays **mirrored**, so it behaves like a mirror rather than like a
 * monitor. The recorded file is not mirrored — that is the part which is easy to
 * get wrong and impossible to notice until somebody reads a sign on camera — and
 * it is not a thing this component has to arrange: the flip is a CSS transform on
 * the element, and the stream handed to `MediaRecorder` is the same object with
 * nothing between it and the recorder.
 */
function CameraStageView({
  stage,
  preview,
  devices,
  circles,
  onGrant,
  onRetry,
}: {
  stage: CameraStage;
  preview: { stream: MediaStreamLike | null; error: string | null };
  devices: DeviceState | null;
  circles: DeviceCircles | null;
  onGrant: () => void;
  onRetry: () => void;
}): JSX.Element {
  const { stream } = preview;
  const video = useSelfView(stream);
  const aspect = useFrameAspect(video);
  const level = useMicLevel(stream);
  const well = stageWell(stage, preview.error);

  const camera = devices?.resolved.videoinput.device;
  // `live` rather than "we have ever had a reading": the aspect is remembered
  // across a camera being switched off, so that the well does not collapse to the
  // column and jump back, but a well with no picture in it is not a frame.
  const framed = stage === "live" && aspect !== null;

  return (
    <section
      className={framed ? "web-stage has-frame" : "web-stage"}
      style={framed ? ({ "--frame": `${aspect}` } as CSSProperties) : undefined}
    >
      <video
        className="web-stage-video"
        ref={video}
        muted
        playsInline
        // The self-view is a picture of you, not a monitor of the take. Its own
        // audio would be your voice a fraction of a second late, in front of a
        // microphone.
        autoPlay
      />
      {well !== null && (
        <div className={`web-stage-well is-${stage}`}>
          <WellGlyph glyph={well.glyph} />
          {well.says !== undefined && (
            <p className="web-stage-says">{well.says}</p>
          )}
          {well.detail !== undefined && (
            <p className="web-stage-detail">{well.detail}</p>
          )}
          {well.action !== undefined && (
            <button
              type="button"
              className={
                well.action.kind === "grant"
                  ? "web-stage-grant"
                  : "web-stage-retry"
              }
              onClick={well.action.kind === "grant" ? onGrant : onRetry}
            >
              {well.action.label}
            </button>
          )}
        </div>
      )}
      {stage === "live" && (
        <div className="web-stage-foot">
          <MicMeter level={level} />
          {camera !== undefined && camera !== null && (
            <span className="web-stage-label">{displayLabel(camera)}</span>
          )}
        </div>
      )}
      {/* The two switches, on the thing they are about (flow ticket 04).
        *
        * Drawn wherever there is something pressable, which the host decides with
        * `stageControls` — **never a stage name checked here**, because the two
        * the ticket names are not the two that have a switch. A well offering a
        * press of its own gets no circles: a grant button and two dead circles in
        * the same box is two answers to one question.
        *
        * Drawn after `.web-stage-foot` in the tree and with no `z-index` on
        * either — two positioned siblings in one stacking context paint in tree
        * order, and a context opened here would be one somebody later has to
        * undo. The foot is `pointer-events: none` besides, so where the scrim
        * reaches over a circle it is still the circle under the pointer. */}
      {circles !== null && (
        <div className="web-stage-controls" role="group" aria-label="Devices">
          <DeviceCircle
            kind="microphone"
            className="web-stage-mic"
            device={circles.microphone}
          />
          <DeviceCircle
            kind="camera"
            className="web-stage-camera"
            device={circles.camera}
          />
        </div>
      )}
    </section>
  );
}

/**
 * The four glyphs the well can show.
 *
 * The pulse is three spans rather than an animated glyph, so "waiting" reads as a
 * process rather than as an icon. The `avatar` is the same camera-off drawing in
 * a filled circle, which is the difference between a *state* and a *slot*: the
 * other three sit on a stage that is telling you something, and this one stands
 * where the picture goes.
 */
function WellGlyph({
  glyph,
}: {
  glyph: "camera" | "camera-off" | "dots" | "avatar";
}): JSX.Element {
  if (glyph === "dots") {
    return (
      <span className="web-stage-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    );
  }
  if (glyph === "avatar") {
    return (
      <span className="web-stage-avatar" aria-hidden="true">
        <CameraOffIcon />
      </span>
    );
  }
  return (
    <span className="web-stage-glyph" aria-hidden="true">
      {glyph === "camera" ? <CameraIcon /> : <CameraOffIcon />}
    </span>
  );
}

/** How many bars, out of six. */
const METER_BARS = 6;

/**
 * That the microphone is hearing you, before the take rather than after it.
 *
 * `aria-hidden`, deliberately: a live level is a continuously changing value
 * that no screen reader should be made to announce, and what it says — the
 * microphone is open and working — is already carried by the mic button's own
 * pressed state.
 */
function MicMeter({ level }: { level: number | null }): JSX.Element {
  const lit = meterBars(level, METER_BARS);
  return (
    <span className="web-stage-meter" aria-hidden="true">
      {Array.from({ length: METER_BARS }, (_unused, index) => (
        <span
          key={index}
          className={index < lit ? "web-stage-bar is-lit" : "web-stage-bar"}
        />
      ))}
    </span>
  );
}

/**
 * The script, as one box (flow ticket 03).
 *
 * **Whatever is in the box is the script.** This card used to be a segmented
 * control over two forms — Paste and Google Doc — with a piece of state deciding
 * which face was up and an effect settling that choice when a link arrived. The
 * question it asked, *where does your script come from*, is not one anybody
 * arrives wanting to answer. There is one script; a Google Doc is a way of
 * filling the box that holds it, which is a button and a dialog rather than half
 * of a decision.
 *
 * The settling effect went with the tabs and did not need replacing. It existed
 * because `?doc=` and `getLastDocUrl()` both arrive an effect *after* mount, so a
 * face derived at mount would have swapped a form out from under a cursor a turn
 * later. Nothing derives a face any more. `link` still arrives late and is still
 * read — by the dialog's field, which is not on screen when it lands.
 *
 * There is no `Load script` button here since that ticket, and flow ticket 05
 * gave the load to {@link StartButton} under the card — which is the only action
 * this screen keeps. "Load" was never a thing anybody wanted to do; it was a thing
 * the product needed doing before it would let you record.
 */
function ScriptCard({
  link,
  text,
  loading,
  error,
  summary,
  onLink,
  onText,
  onLoad,
  onClear,
}: {
  link: string;
  text: string;
  loading: boolean;
  error?: string;
  summary: string | null;
  onLink: (link: string) => void;
  onText: (text: string) => void;
  onLoad: (reference: string) => Promise<LoadResult>;
  onClear: () => void;
}): JSX.Element {
  const [asking, setAsking] = useState(false);

  /**
   * Fetch the document, and put it in the box.
   *
   * `setText` with the text the load returned, rather than listening for
   * `onScriptLoaded`. That broadcast fires for a *paste* too — it is the worker
   * saying what it now holds, whatever put it there — so a box wired to it would
   * be overwritten under somebody who was typing in it. This is the one path that
   * knows a document was asked for.
   *
   * The dialog closes either way. On a refusal the sentence that says what to
   * change is `.web-error` in the card *behind* it, which is where every other
   * load failure on this screen has always landed, and a dialog left standing
   * over it would be hiding the only thing worth reading.
   */
  const loadDoc = async (): Promise<void> => {
    // Only over something worth losing, and asked *before* the fetch: a confirm
    // after the network has already answered is a question about work already
    // done.
    if (text !== "" && !window.confirm(OVERWRITE_CONFIRM)) return;
    const result = await onLoad(link);
    setAsking(false);
    if (result.ok) onText(result.text);
  };

  // Asked on the same condition as the overwrite: an empty box over a loaded
  // script has nothing on screen to lose, and a question about nothing is one
  // people learn to press through.
  const clear = (): void => {
    if (text !== "" && !window.confirm(CLEAR_CONFIRM)) return;
    onClear();
  };

  return (
    <section className="web-script">
      <div className="web-script-head">
        {/* Only over an empty box, so it never overwrites and asks nothing. It
          * fills the box and stops there: START loads it, as for a paste.
          * It needs no guard for a take in progress, because the room is not on
          * screen then: a take moves the stage to `prompting`, and both ways
          * back here are disabled while `takeInProgress` (`topBarButtons` in
          * `prompter.ts`, and the menu's `leaving` in `control-bar.ts`). */}
        {text === "" && (
          <button
            type="button"
            className="web-script-sample"
            onClick={() => onText(SAMPLE_SCRIPT)}
          >
            {TRY_A_SAMPLE}
          </button>
        )}
        <button
          type="button"
          className="web-script-doc"
          onClick={() => setAsking(true)}
        >
          {LOAD_FROM_DOC}
        </button>
      </div>

      <textarea
        id="script-text"
        className="web-script-box"
        value={text}
        spellCheck={false}
        // Not "Paste your script" any more. Pasting was one of two things this
        // box could have been; it *is* the script now, and the label says so.
        aria-label="Your script"
        placeholder={
          "Paste your script. Notes in [square brackets] and lines " +
          "starting with # are shown but never read aloud."
        }
        onChange={(event) => onText(event.target.value)}
      />

      {error !== undefined && (
        <p className="web-error" role="alert">
          {error}
        </p>
      )}

      {/* Clear is on anything in the box, not only on a loaded script. A script
        * typed in and not yet started, or one a reload put back in the box, is
        * not loaded — and with the button on `summary` alone, neither could be
        * cleared except by selecting it all and deleting it, which also left
        * the remembered copy in storage. */}
      {(summary !== null || text !== "") && (
        <p className="web-script-foot">
          {summary !== null && <span className="web-script-summary">{summary}</span>}
          <button type="button" className="web-script-clear" onClick={clear}>
            Clear
          </button>
        </p>
      )}

      {asking && (
        <DocModal
          link={link}
          loading={loading}
          onLink={onLink}
          onLoad={() => void loadDoc()}
          onClose={() => setAsking(false)}
        />
      )}
    </section>
  );
}

/**
 * Where the URL goes, now that there is no second form to put it in.
 *
 * The field and the sharing hint are the old `web-link` form's, unchanged — what
 * moved is only the surface they are on. {@link Modal} supplies every part of the
 * behaviour, so the one thing decided here is that `Load` is the affirmative and
 * `Cancel` is the same press as Escape and the `✕`.
 *
 * The field is bound to the host's `link`, not to a copy: a shared `?doc=` and a
 * remembered document both land there, an effect after mount, and reopening this
 * dialog has to show what the page already knows rather than an empty box.
 */
function DocModal({
  link,
  loading,
  onLink,
  onLoad,
  onClose,
}: {
  link: string;
  loading: boolean;
  onLink: (link: string) => void;
  onLoad: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal
      title={LOAD_FROM_DOC}
      onClose={onClose}
      buttons={
        <>
          <button type="button" className="web-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="doc-form"
            className="web-modal-go"
            disabled={loading || link.trim() === ""}
          >
            {loading ? "Loading…" : "Load"}
          </button>
        </>
      }
    >
      {/* A form so that Enter in the field is Load, which is what a one-field
        * dialog has to do. The button is outside it, in the footer, and reaches
        * back by `form=` — the alternative is a second click handler saying the
        * same thing as the submit. */}
      <form
        id="doc-form"
        className="web-doc-form"
        onSubmit={(event) => {
          event.preventDefault();
          onLoad();
        }}
      >
        <input
          id="doc-link"
          type="text"
          value={link}
          spellCheck={false}
          aria-label="Google Doc link"
          placeholder="https://docs.google.com/document/d/…"
          onChange={(event) => onLink(event.target.value)}
        />
        {/* Said before the load fails rather than only after it: the sharing
          * setting is the one thing about this a first-time visitor cannot
          * guess. */}
        <p className="web-hint">{DOC_SHARING_HINT}</p>
      </form>
    </Modal>
  );
}

/**
 * The room's one action (flow ticket 05).
 *
 * It hands whatever is in the box to the host's loader and opens the prompter.
 * **It does not start the take** — the record button on the prompter's control
 * bar does that, when the reader is ready. There is a gap between having a script
 * and being ready to read it to camera, and the room is the wrong side of the
 * door to spend it on. It is also the only way into the prompter now, which is
 * why nothing stands beside it: a rehearsal is reached by pressing this and then
 * rehearsing, one press fewer than the `⋯` row it replaced.
 *
 * Ordinary page furniture rather than a dock. The room has no floating bar any
 * more, and this is not standing in for the one that left, so it does not try to
 * look like it.
 *
 * Everything it says and every reason it is dead is {@link startButton} in
 * `green-room.ts`, tested at that seam — this draws the answer. The `title` is the
 * only place a disabled control can explain itself, and it is absent where the
 * decision has nothing worth saying.
 */
function StartButton({
  text,
  loading,
  onStart,
}: {
  text: string;
  loading: boolean;
  onStart: () => void;
}): JSX.Element {
  const button = startButton({ text, loading });
  return (
    <button
      type="button"
      className="web-room-start"
      disabled={button.disabled}
      {...(button.reason === undefined ? {} : { title: button.reason })}
      onClick={onStart}
    >
      {button.label}
    </button>
  );
}

